import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mfaLoginVerify, clearMfaRequired, clearError } from '../../redux/authentication/utils/core/authslice';

const CODE_LENGTH = 6;

const MFACodeEntryPage = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const inputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error: authError, mfaRequired } = useSelector((s) => s.auth);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  // Landed here directly (refresh, bookmark) without a pending MFA login --
  // the session_id cookie the backend needs is either missing or expired
  // (5 min TTL), so there's nothing to verify against. Send back to login.
  useEffect(() => {
    if (!mfaRequired && !isAuthenticated && !loading) {
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authError) setLocalError(authError);
  }, [authError]);

  useEffect(() => () => { dispatch(clearError()); }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) {
      setLocalError(t('auth.mfa.errors.codeLength', { length: CODE_LENGTH }));
      return;
    }
    setLocalError('');
    try {
      await dispatch(mfaLoginVerify(code)).unwrap();
    } catch {
      setCode('');
    }
  };

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (localError) setLocalError('');
  };

  const handleBack = () => {
    dispatch(clearMfaRequired());
    navigate('/login', { replace: true });
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ background: '#09090f', fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 900,
          height: 480,
          background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      <button
        onClick={handleBack}
        className="absolute top-6 left-6 flex items-center text-violet-300 hover:text-violet-100 transition-colors z-50"
      >
        <ArrowLeft className="w-5 h-5 mr-1" /> {t('auth.back')}
      </button>

      <div className="relative w-full max-w-md z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-violet-300" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-2">{t('auth.mfa.heading')}</h2>
          <p className="text-gray-400">{t('auth.mfa.subtitle')}</p>
        </div>

        <div className="rounded-3xl p-8 shadow-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={handleCodeChange}
              maxLength={CODE_LENGTH}
              className="w-full py-4 bg-white/5 rounded-2xl text-white text-center text-3xl tracking-[0.5em] placeholder-gray-600 border border-white/10 outline-none focus:border-violet-400 transition-colors"
            />

            {localError && (
              <div className="p-3 rounded-lg text-sm border bg-red-600/30 border-red-500 text-red-200">
                {localError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || code.length !== CODE_LENGTH}
              className="w-full py-4 text-white font-semibold rounded-2xl shadow-lg disabled:opacity-50 transition-all duration-200 active:scale-[0.98]"
              style={{ background: '#7c3aed' }}
            >
              {loading ? t('auth.mfa.verifying') : t('auth.mfa.submit')}
            </button>
          </form>

          <div className="mt-6 text-center text-gray-400 text-sm">
            {t('auth.mfa.havingTrouble')} <Link to="/login" onClick={() => dispatch(clearMfaRequired())} className="text-violet-400 hover:text-violet-300 font-medium transition-colors">{t('auth.mfa.backToSignIn')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFACodeEntryPage;
